import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageTabs, type PageTabItem } from "@/components/ui/page-tabs";
import { Pencil, Download, FileText, LayoutDashboard, LineChart, Handshake } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface DealHeaderProps {
  dealId: string;
}

type TabValue = "results" | "detail" | "sensitivity" | "negotiation";

const TAB_ITEMS = (id: string): (PageTabItem & { value: TabValue })[] => [
  { value: "results", label: "IC memo", icon: FileText, href: `/deals-v2/${id}/results` },
  { value: "detail", label: "Detail view", icon: LayoutDashboard, href: `/deals-v2/${id}/detail` },
  { value: "sensitivity", label: "Sensitivity", icon: LineChart, href: `/deals-v2/${id}/sensitivity` },
  { value: "negotiation", label: "Negotiation", icon: Handshake, href: `/deals-v2/${id}/negotiation` },
];

export function DealHeader({ dealId }: DealHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [deal, setDeal] = useState<{ name: string | null; status: string | null } | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    (async () => {
      const { data } = await supabase
        .from("deals")
        .select("name, status")
        .eq("id", dealId)
        .maybeSingle();
      if (!cancelled) {
        setDeal({ name: data?.name ?? null, status: data?.status ?? null });
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId]);

  const path = location.pathname;
  const activeTab: TabValue | null =
    path.endsWith("/results") ? "results"
    : path.endsWith("/detail") ? "detail"
    : path.endsWith("/sensitivity") ? "sensitivity"
    : path.endsWith("/negotiation") ? "negotiation"
    : null;

  const showTabs = deal?.status === "complete";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap min-h-[2.5rem]">
        {loaded ? (
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight truncate">
            {deal?.name ?? "Untitled Deal"}
          </h1>
        ) : (
          <Skeleton className="h-9 w-72 max-w-full" />
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(`/deals-v2/${dealId}`)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit deal
          </Button>
          <Button size="sm" onClick={() => navigate(`/deals-v2/${dealId}/export`)}>
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export
          </Button>
        </div>
      </div>
      <div className="min-h-[2.25rem]">
        {showTabs && <PageTabs items={TAB_ITEMS(dealId)} activeValue={activeTab} />}
      </div>
    </div>
  );
}

/**
 * Hook: returns { status, loading } for a deal. Used by analysis pages to
 * gate rendering until the wizard is complete.
 */
export function useDealStatus(dealId: string | undefined) {
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dealId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("deals")
        .select("status")
        .eq("id", dealId)
        .maybeSingle();
      if (!cancelled) {
        setStatus(data?.status ?? null);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dealId]);

  return { status, loading };
}
